/** „Wir beide" — das Brett mit den Büchern, die in beiden Regalen stehen.
 *
 *  Hier wird nichts zusammengeführt: Es sind zwei getrennte Einträge, die
 *  nebeneinander gezeigt werden. Jede Bewertung, jede Notiz bleibt bei ihrem
 *  Besitzer. Erkannt wird das Paar über ISBN oder über normalisierten Titel +
 *  Autor — so werden auch verschiedene Ausgaben desselben Werks gefunden.
 */

import { useEffect, useState } from "react";
import type { Book } from "../../../shared/types";
import { STATUS_LABEL } from "../../../shared/types";
import * as api from "../lib/api";
import { useStore } from "../lib/store";

function Seite({ book, name }: { book: Book; name: string }) {
  return (
    <div style={{ flex: "1 1 200px" }}>
      <span className="feldname">{name}</span>
      <p className="lauftext" style={{ fontSize: 14, margin: "0 0 6px" }}>
        {STATUS_LABEL[book.status]}
        {book.read_year ? ` · ${book.read_year}` : ""}
        {book.fav ? " · ★ Liebling" : ""}
      </p>
      <p className="lauftext" style={{ fontSize: 14, margin: "0 0 6px" }}>
        {book.rating != null ? (
          <strong>{book.rating}/10</strong>
        ) : book.stars != null ? (
          <span title="aus Goodreads, nicht hochgerechnet">
            {book.stars} von 5 Sternen
          </span>
        ) : (
          <span style={{ opacity: 0.55 }}>nicht bewertet</span>
        )}
      </p>
      {book.notes && (
        <p
          className="lauftext"
          style={{ fontSize: 14, fontStyle: "italic", opacity: 0.85 }}
        >
          „{book.notes}"
        </p>
      )}
    </div>
  );
}

export function WirBeide() {
  const { modus } = useStore();
  const [paare, setPaare] = useState<
    { key: string; sarah: Book; mario: Book }[]
  >([]);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    if (modus !== "server") {
      setLaedt(false);
      setFehler("Ohne Server lässt sich das gemeinsame Brett nicht berechnen.");
      return;
    }
    api
      .together()
      .then((res) => setPaare(res.data))
      .catch((err) => setFehler(err.message))
      .finally(() => setLaedt(false));
  }, [modus]);

  return (
    <>
      <section style={{ maxWidth: "46ch" }}>
        <h2 className="titelzeile">Wir beide</h2>
        <p className="lauftext">
          Bücher, die in beiden Regalen stehen — mit beiden Bewertungen und
          beiden Notizen nebeneinander. Zusammengeführt wird nichts: Es bleiben
          zwei Einträge, die nur wissen, dass es den anderen gibt.
        </p>
        {!laedt && !fehler && (
          <p className="klein" style={{ marginTop: 16 }}>
            {paare.length} gemeinsame Titel
          </p>
        )}
      </section>

      {laedt && <p className="lauftext">lädt …</p>}
      {fehler && (
        <div
          className="karte"
          style={{ borderLeft: "3px solid var(--warn)", marginTop: 20 }}
        >
          {fehler}
        </div>
      )}

      <div style={{ display: "grid", gap: 16, marginTop: 34 }}>
        {paare.map(({ key, sarah, mario }) => {
          const bild =
            api.coverUrl(sarah.cover_key ?? sarah.isbn, "s") ??
            api.coverUrl(mario.cover_key ?? mario.isbn, "s");
          return (
            <article
              key={key}
              className="karte"
              style={{ display: "flex", gap: 20, flexWrap: "wrap" }}
            >
              {bild && (
                <img
                  src={bild}
                  alt=""
                  style={{
                    width: 84,
                    height: "auto",
                    alignSelf: "flex-start",
                    boxShadow: "var(--sh-near)",
                  }}
                  loading="lazy"
                />
              )}
              <div style={{ flex: "3 1 320px" }}>
                <h3
                  style={{
                    fontFamily: "var(--display)",
                    fontWeight: 400,
                    fontSize: 21,
                    margin: "0 0 4px",
                  }}
                >
                  {sarah.title}
                </h3>
                <p className="klein" style={{ marginBottom: 14 }}>
                  {sarah.author}
                  {sarah.isbn !== mario.isbn && " · verschiedene Ausgaben"}
                </p>
                <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                  <Seite book={sarah} name="Sarah" />
                  <Seite book={mario} name="Mario" />
                </div>
              </div>
            </article>
          );
        })}
      </div>

      {!laedt && !fehler && !paare.length && (
        <p className="lauftext" style={{ marginTop: 30 }}>
          Noch kein gemeinsamer Titel gefunden.
        </p>
      )}
    </>
  );
}
